import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';
import { canCreateDocument, getDocumentLimit } from '../lib/planLimits';
import PaywallModal from '../components/PaywallModal';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f9fafb',
  green:  '#16a34a',
};

const DOC_TYPES = ['Mietvertrag', 'Nebenkostenabrechnung', 'Übergabeprotokoll', 'Kündigung', 'Mahnung', 'Rechnung', 'Sonstiges'];
const ZEITRAUM_OPTIONS = ['Alle', 'Heute', 'Diese Woche', 'Diesen Monat', 'Dieses Jahr'];

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–';
}

function filterByZeitraum(docs, zeitraum) {
  if (zeitraum === 'Alle') return docs;
  const now = new Date();
  return docs.filter(d => {
    const dt = new Date(d.created_at);
    if (zeitraum === 'Heute') return dt.toDateString() === now.toDateString();
    if (zeitraum === 'Diese Woche') {
      const start = new Date(now); start.setDate(now.getDate() - now.getDay());
      return dt >= start;
    }
    if (zeitraum === 'Diesen Monat') return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
    if (zeitraum === 'Dieses Jahr') return dt.getFullYear() === now.getFullYear();
    return true;
  });
}

/* ── File icon ── */
function FileIcon({ name = '', size = 32 }) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  const isPdf = ext === 'pdf';
  const isDoc = ['doc','docx'].includes(ext);
  const isImg = ['jpg','jpeg','png','gif','webp'].includes(ext);
  const color = isPdf ? '#ef4444' : isDoc ? '#111' : isImg ? '#888' : T.muted;
  const label = isPdf ? 'PDF' : isDoc ? 'DOC' : isImg ? 'IMG' : ext.toUpperCase() || 'FILE';
  return (
    <div style={{ width: size, height: size, borderRadius: 6, background: `${color}18`, border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ fontSize: size < 28 ? 8 : 10, fontWeight: 800, color, letterSpacing: '0.02em' }}>{label}</span>
    </div>
  );
}

/* ── Document row ── */
function DocRow({ doc, onDelete, last }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '36px 1fr 140px 100px 100px 40px',
      alignItems: 'center', padding: '12px 20px',
      borderBottom: last ? 'none' : `1px solid ${T.border}`,
      background: 'white', transition: 'background 0.1s', gap: 8,
    }}
    onMouseEnter={e => e.currentTarget.style.background = T.bg}
    onMouseLeave={e => e.currentTarget.style.background = 'white'}
    >
      <FileIcon name={doc.file_name || doc.name} />
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 2 }}>{doc.name}</div>
        {doc.file_name && doc.file_name !== doc.name && <div style={{ fontSize: 12, color: T.muted }}>{doc.file_name}</div>}
      </div>
      <div style={{ fontSize: 13, color: T.muted }}>{doc.type || '–'}</div>
      <div style={{ fontSize: 12, color: T.muted }}>{formatBytes(doc.file_size)}</div>
      <div style={{ fontSize: 12, color: T.muted }}>{fmtDate(doc.created_at)}</div>
      <div style={{ position: 'relative' }}>
        <button onClick={() => setMenuOpen(o => !o)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 5, color: T.muted, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
          {[0,1,2].map(i => <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: T.muted }} />)}
        </button>
        {menuOpen && (
          <div style={{ position: 'absolute', right: 0, top: '100%', background: 'white', border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 20, minWidth: 140, padding: 4 }}>
            {doc.file_url && (
              <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', padding: '8px 14px', fontSize: 13, color: T.ink, textDecoration: 'none', borderRadius: 5 }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >Öffnen</a>
            )}
            <button onClick={() => { onDelete(doc); setMenuOpen(false); }} style={{ width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 5 }}
              onMouseEnter={e => e.currentTarget.style.background = '#fff1f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >Löschen</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Upload zone ── */
function UploadZone({ onUpload }) {
  const fileRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  function handleDrop(e) {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onUpload(file);
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${dragging ? T.ink : '#86efac'}`,
        borderRadius: 10, padding: '32px 24px', textAlign: 'center',
        background: dragging ? '#f5f5f5' : '#f0fdf4',
        transition: 'all 0.15s', cursor: 'default', marginBottom: 28,
      }}
    >
      <button
        onClick={() => fileRef.current?.click()}
        className="btn-primary" style={{ marginBottom: 12 }}
      >
        Dokumente hochladen
      </button>
      <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.7 }}>
        oder via drag &amp; drop hereinkopieren<br />
        Die Dateigröße darf 25 MB nicht überschreiten.
      </div>
      <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) onUpload(e.target.files[0]); }} />
    </div>
  );
}

/* ── Upload modal ── */
function UploadModal({ file, properties, tenants, kategorie, onClose, onSave }) {
  const [name,       setName]       = useState(file?.name?.replace(/\.[^/.]+$/, '') || '');
  const [type,       setType]       = useState('Mietvertrag');
  const [propertyId, setPropertyId] = useState('');
  const [tenantId,   setTenantId]   = useState('');
  const [notes,      setNotes]      = useState('');
  const [saving,     setSaving]     = useState(false);

  const inp = { width: '100%', padding: '9px 12px', borderRadius: 7, border: `1px solid ${T.border}`, fontSize: 14, color: T.ink, outline: 'none', boxSizing: 'border-box', background: 'white' };
  const lbl = { fontSize: 12, fontWeight: 600, color: T.muted, marginBottom: 5, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: 'white', borderRadius: 14, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: T.ink }}>Dokument hochladen</h2>
          {file && <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{file.name} · {formatBytes(file.size)}</div>}
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, overflow: 'auto' }}>
          <div>
            <label style={lbl}>Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} style={inp} placeholder="Dokumentenname" />
          </div>
          <div>
            <label style={lbl}>Typ</label>
            <select value={type} onChange={e => setType(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Objekt</label>
              <select value={propertyId} onChange={e => setPropertyId(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Kein Objekt</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Mieter</label>
              <select value={tenantId} onChange={e => setTenantId(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Kein Mieter</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={lbl}>Notizen</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Optionale Notizen..." />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} className="btn-secondary">Abbrechen</button>
          <button onClick={() => onSave({ name, type, propertyId, tenantId, notes, kategorie })} disabled={!name.trim() || saving}
            className="btn-primary" style={{ opacity: (!name.trim() || saving) ? 0.6 : 1 }}>
            {saving ? 'Hochladen…' : 'Hochladen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   Main Documents page
══════════════════════════════════════ */
export default function Documents() {
  const { user, subscription } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [showPaywall, setShowPaywall] = useState(false);
  const [docs,        setDocs]        = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants,    setTenants]    = useState([]);
  const [loading,    setLoading]    = useState(true);

  /* nav state */
  const [section,    setSection]    = useState('eingang'); // 'eingang' | 'property:{id}' | 'sonstiges'
  const [propOpen,   setPropOpen]   = useState(true);

  /* filters */
  const [search,     setSearch]     = useState('');
  const [zeitraum,   setZeitraum]   = useState('Alle');
  const [openZeit,   setOpenZeit]   = useState(false);

  /* upload */
  const [pendingFile, setPendingFile] = useState(null);
  const [uploading,   setUploading]   = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [dRes, pRes, tRes] = await Promise.all([
      supabase.from('documents').select('*, properties(id,name), tenants(id,first_name,last_name)').eq('owner_id', user.id).order('created_at', { ascending: false }),
      supabase.from('properties').select('id,name,city,street').eq('owner_id', user.id).order('name'),
      supabase.from('tenants').select('id,first_name,last_name,property_id').eq('owner_id', user.id).order('last_name'),
    ]);
    setDocs(dRes.data || []);
    setProperties(pRes.data || []);
    setTenants(tRes.data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  /* close zeitraum dropdown on outside click */
  useEffect(() => {
    const close = () => setOpenZeit(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  /* derive current docs for the selected section */
  const currentKat = section === 'eingang' ? 'eingang' : section === 'sonstiges' ? 'sonstiges' : null;
  const currentPropId = section.startsWith('property:') ? section.split('property:')[1] : null;

  const sectionDocs = docs.filter(d => {
    if (currentPropId) return d.property_id === currentPropId;
    if (currentKat === 'sonstiges') return d.kategorie === 'sonstiges' && !d.property_id;
    return !d.property_id && d.kategorie !== 'sonstiges'; // eingang
  });

  const filtered = filterByZeitraum(
    search ? sectionDocs.filter(d => (d.name || '').toLowerCase().includes(search.toLowerCase()) || (d.file_name || '').toLowerCase().includes(search.toLowerCase())) : sectionDocs,
    zeitraum
  );

  /* Counts per property for badge */
  const propDocCount = docs.filter(d => d.property_id).reduce((acc, d) => {
    acc[d.property_id] = (acc[d.property_id] || 0) + 1;
    return acc;
  }, {});
  const totalObjektDocs = Object.values(propDocCount).reduce((s, v) => s + v, 0);

  /* Handle file selection → show modal */
  function handleFileSelect(file) {
    if (file.size > 25 * 1024 * 1024) { addToast('Datei zu groß (max. 25 MB)', 'error'); return; }
    setPendingFile(file);
  }

  /* Upload */
  async function handleUpload({ name, type, propertyId, tenantId, notes, kategorie }) {
    if (!pendingFile) return;
    if (!canCreateDocument(subscription, docs.length)) { setShowPaywall(true); return; }
    setUploading(true);
    const filePath = `${user.id}/${Date.now()}_${pendingFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const { error: upErr } = await supabase.storage.from('documents').upload(filePath, pendingFile);
    if (upErr) { addToast('Upload fehlgeschlagen: ' + upErr.message, 'error'); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(filePath);

    // determine final property_id and kategorie
    const finalPropId = propertyId || currentPropId || null;
    const finalKat    = finalPropId ? 'eingang' : (kategorie || (section === 'sonstiges' ? 'sonstiges' : 'eingang'));

    const { error: dbErr } = await supabase.from('documents').insert({
      owner_id: user.id, name: name.trim(), type,
      property_id: finalPropId, tenant_id: tenantId || null,
      notes: notes.trim() || null,
      file_path: filePath, file_url: publicUrl,
      file_name: pendingFile.name, file_size: pendingFile.size,
      file_ext: pendingFile.name.split('.').pop().toLowerCase(),
      kategorie: finalKat, url: publicUrl,
    });
    if (dbErr) { await supabase.storage.from('documents').remove([filePath]); addToast('Fehler: ' + dbErr.message, 'error'); setUploading(false); return; }
    addToast('Dokument hochgeladen', 'success');
    setPendingFile(null); setUploading(false);
    load();
  }

  async function handleDelete(doc) {
    if (!window.confirm(`"${doc.name}" wirklich löschen?`)) return;
    if (doc.file_path) await supabase.storage.from('documents').remove([doc.file_path]);
    await supabase.from('documents').delete().eq('id', doc.id).eq('owner_id', user.id);
    addToast('Dokument gelöscht', 'success');
    load();
  }

  const sectionTitle = section === 'eingang' ? 'Eingang' : section === 'sonstiges' ? 'Sonstiges' : (properties.find(p => p.id === currentPropId)?.name || 'Objekt');

  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", overflow: 'hidden' }}>

      {/* ── Left sub-nav ── */}
      {!isMobile && (
        <div style={{ width: 240, flexShrink: 0, borderRight: `1px solid ${T.border}`, background: 'white', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '24px 20px 16px', flexShrink: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>Dokumente</div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '0 8px 16px' }}>
            {/* Eingang */}
            <NavItem label="Eingang" active={section === 'eingang'} onClick={() => setSection('eingang')} />

            <div style={{ height: 1, background: T.border, margin: '8px 8px' }} />

            {/* Objekte */}
            <div
              onClick={() => setPropOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 7, cursor: 'pointer', userSelect: 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = T.bg}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>Objekte</span>
                {properties.length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, background: T.ink, color: 'white', borderRadius: 99, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>
                    {properties.length}
                  </span>
                )}
              </div>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5" strokeLinecap="round">
                <polyline points={propOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
              </svg>
            </div>
            {propOpen && properties.map(p => (
              <NavItem
                key={p.id}
                label={p.name}
                active={section === `property:${p.id}`}
                onClick={() => setSection(`property:${p.id}`)}
                indent
                badge={propDocCount[p.id] || null}
              />
            ))}
            {propOpen && properties.length === 0 && (
              <div style={{ fontSize: 12, color: T.muted, padding: '4px 20px 8px' }}>Keine Objekte</div>
            )}

            <div style={{ height: 1, background: T.border, margin: '8px 8px' }} />

            {/* Sonstiges */}
            <NavItem label="Sonstiges" active={section === 'sonstiges'} onClick={() => setSection('sonstiges')} />
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '16px' : '28px 32px' }}>
        {/* Header */}
        <h1 style={{ fontSize: 28, fontWeight: 800, color: T.ink, margin: '0 0 20px', letterSpacing: '-0.6px' }}>{sectionTitle}</h1>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, alignItems: 'center' }} onMouseDown={e => e.stopPropagation()}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Dokumente durchsuchen"
              style={{ width: '100%', paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8, borderRadius: 7, border: `1px solid ${T.border}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          {/* Zeitraum dropdown */}
          <div style={{ position: 'relative' }} onMouseDown={e => e.stopPropagation()}>
            <button onClick={() => setOpenZeit(o => !o)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
              borderRadius: 7, border: `1px solid ${T.border}`, background: 'white',
              fontSize: 13, color: T.ink, cursor: 'pointer', fontWeight: 500,
            }}>
              Zeitraum: <span style={{ fontWeight: 700 }}>{zeitraum}</span>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {openZeit && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'white', border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 20, minWidth: 160, padding: 6 }}>
                {ZEITRAUM_OPTIONS.map(z => (
                  <div key={z} onClick={() => { setZeitraum(z); setOpenZeit(false); }}
                    style={{ padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: zeitraum === z ? 700 : 400, color: zeitraum === z ? T.ink : T.ink, background: zeitraum === z ? '#f5f5f5' : 'transparent' }}
                    onMouseEnter={e => { if (zeitraum !== z) e.currentTarget.style.background = T.bg; }}
                    onMouseLeave={e => { if (zeitraum !== z) e.currentTarget.style.background = 'transparent'; }}
                  >{z}</div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upload zone */}
        <UploadZone onUpload={handleFileSelect} />

        {/* Section heading */}
        <h2 style={{ fontSize: 18, fontWeight: 700, color: T.ink, margin: '0 0 16px' }}>{sectionTitle}</h2>

        {/* Eingang info banner */}
        {section === 'eingang' && filtered.length === 0 && (
          <div style={{ borderRadius: 10, background: '#111', padding: '32px 40px', textAlign: 'center', maxWidth: 900 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'white', marginBottom: 12 }}>Sende Deine ersten Dokumente in Deinen persönlichen Posteingang</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, maxWidth: 640, margin: '0 auto' }}>
              Lade Dokumente direkt über den Button oben hoch oder ziehe sie per Drag &amp; Drop in den Eingang. Du kannst hier Verträge, Rechnungen und andere Dokumente ablegen und ihnen Objekte oder Mieter zuweisen.
            </div>
          </div>
        )}

        {/* Document list */}
        {!loading && filtered.length > 0 && (
          <div style={{ background: 'white', borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 140px 100px 100px 40px', padding: '10px 20px', background: T.bg, borderBottom: `1px solid ${T.border}`, gap: 8 }}>
              {['', 'Name', 'Typ', 'Größe', 'Datum', ''].map((h, i) => (
                <div key={i} style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
              ))}
            </div>
            {filtered.map((d, i) => (
              <DocRow key={d.id} doc={d} onDelete={handleDelete} last={i === filtered.length - 1} />
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && section !== 'eingang' && (
          <div style={{ padding:'48px 40px' }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:32, marginBottom:36 }}>
              <div style={{ flexShrink:0, width:64, height:64, background:'#f7f7f7', borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid e5e5e5' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:20, fontWeight:700, color:T.ink, marginBottom:8, letterSpacing:'-0.3px' }}>Noch keine Dokumente vorhanden</div>
                <div style={{ fontSize:14, color:T.muted, lineHeight:1.8, maxWidth:520 }}>Lade Mietverträge, Übergabeprotokolle, Abrechnungen und weitere Dokumente hoch und verknüpfe sie mit Objekten oder Mietern.</div>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
              {[
                { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>, title:'Mietverträge', text:'Verträge zentral ablegen und jederzeit abrufen.' },
                { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, title:'Pro Objekt', text:'Dokumente direkt einer Immobilie oder einem Mieter zuordnen.' },
                { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>, title:'Einfacher Upload', text:'PDFs, Bilder und weitere Dateiformate hochladen.' },
              ].map((f,i)=>(
                <div key={i} style={{ background:'#f7f7f7', borderRadius:12, padding:'18px 20px', border:`1px solid ${T.border}` }}>
                  <div style={{ width:36, height:36, background:'white', borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:12, border:`1px solid ${T.border}`, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:T.ink, marginBottom:4 }}>{f.title}</div>
                  <div style={{ fontSize:12, color:T.muted, lineHeight:1.6 }}>{f.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upload modal */}
      {pendingFile && (
        <UploadModal
          file={pendingFile}
          properties={properties}
          tenants={tenants}
          kategorie={section === 'sonstiges' ? 'sonstiges' : 'eingang'}
          onClose={() => setPendingFile(null)}
          onSave={handleUpload}
        />
      )}

      {showPaywall && (
        <PaywallModal
          onClose={() => setShowPaywall(false)}
          title="Dokument-Limit erreicht"
          message={`Mit deinem aktuellen Plan kannst du maximal ${getDocumentLimit(subscription)} Dokumente hochladen.`}
        />
      )}
    </div>
  );
}

function NavItem({ label, active, onClick, indent = false, badge }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `8px ${indent ? '28px' : '12px'} 8px ${indent ? '28px' : '12px'}`,
        borderRadius: 7, cursor: 'pointer',
        background: active ? '#f5f5f5' : 'transparent',
        borderRight: active ? `3px solid ${T.ink}` : '3px solid transparent',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = T.bg; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ fontSize: indent ? 13 : 14, fontWeight: active ? 700 : (indent ? 400 : 500), color: active ? T.ink : T.ink }}>
        {label}
      </span>
      {badge && (
        <span style={{ fontSize: 11, fontWeight: 700, background: T.muted, color: 'white', borderRadius: 99, padding: '1px 6px', minWidth: 16, textAlign: 'center' }}>{badge}</span>
      )}
    </div>
  );
}
